import React, { useRef, useLayoutEffect, useEffect } from 'react';

interface UseDebounceHookOptionType {
    // 防抖延迟时间
    wait: number;
    // 是否在在延迟开始前调用, default false
    leading?: boolean;
    // 是否在在延迟结束后调用, default true
    trailing?: boolean;
    // 最大等待时间，单位为毫秒
    maxWait?: number;
}

/**
 * Hook 防抖
 * - 参照 ahooks.useDebounceEffect
 * - url: https://ahooks.js.org/zh-CN/hooks/use-debounce-effect/
 * @param value
 * @param options
 * @example
 *  const debounceText = useDebounceHook(inputValue, { wait: 1000 });
 *  const firstUpdate = React.useRef(true);
 *  React.useEffect(() => {
 *      if (firstUpdate.current) {
 *          firstUpdate.current = false;
 *          return;
 *      }
 *      else {
 *          console.info("change", debounceText);
 *      }
 *  }, [debounceText]);
 */
function useDebounceEffect(value: any, options: UseDebounceHookOptionType) {
    const { leading = false, trailing = true, wait, maxWait } = options
    const [debounceValue, setDebounceValue] = React.useState(value);
    const [deLeading, setDeleading] = React.useState(leading);
    const [maxTimer, setMaxTimer] = React.useState(null as any);

    const setDeVal = () => setDebounceValue(value);

    const setDeValref = useRef(setDeVal);

    // mounted before useEffect
    useLayoutEffect(() => {
        setDeValref.current = setDeVal;
    });

    useEffect(() => {
        if (deLeading) {
            setDeValref.current();
            setDeleading(false);
        }

        if (maxWait && maxWait > wait && !maxTimer) {
            const mt = setTimeout(() => {
                setMaxTimer(null);
                setDeValref.current();
            }, maxWait)
            setMaxTimer(mt)
        }

        const timer = setTimeout(() => {
            if (trailing) setDeValref.current();
            if (leading) setDeleading(true);
            if (maxWait) {
                clearTimeout(maxTimer);
                setMaxTimer(null);
            }
        }, wait);
        return () => clearTimeout(timer)
    }, [value]);
    return debounceValue;
}

export default useDebounceEffect;